import React, { CSSProperties, useEffect, useRef } from 'react';
import { wrapGrid } from 'animate-css-grid';
import { defined } from '@unifocus/js-utils';
import { PaddingType, Panel } from '../panel/Panel';
import { useResizeObserver } from '../../../hooks/ResizeObserverHook';

/**
 * {@docCategory CardGridPanel}
 */
export interface CardGridPanelProps {
   /**
    * Whether or not to enable animation effects
    */
   enableAnimation?: boolean;

   /**
    * The gap between the panels
    *
    * @defaultvalue medium
    */
   gap?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the component has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Determines if the panel will fill it's parent containers width and height.
    *
    * @defaultvalue true
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width.
    *
    * @defaultvalue true
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue true
    */
   verticalFill?: boolean;

   /**
    * The width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if horizontalFill is true; otherwise 'max-content'
    */
   width?: string | number;

   /**
    * The height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if verticalFill is true; otherwise 'max-content'
    */
   height?: string | number;

   /**
    * The minimum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The minimum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;
}

export const CardGridPanel: React.FC<React.PropsWithChildren<CardGridPanelProps>> = (props: React.PropsWithChildren<CardGridPanelProps>) => {
   const cardPanelRef = useRef(null);
   const [dimensionsRef, dimensions] = useResizeObserver();

   const enableAnimation = props.enableAnimation;

   useEffect(() => {
      const currentCardPanelRef = cardPanelRef.current;

      if (enableAnimation && currentCardPanelRef) {
         wrapGrid(currentCardPanelRef, { easing: 'backOut', stagger: 0, duration: 300 });
      }
   },[enableAnimation, cardPanelRef]);

   const width = (dimensions && dimensions.width && dimensions.width >= 0) ? dimensions.width : null;
   const children = React.Children.toArray(props.children);

   if (width === null) {
      const firstChild = children[0];
      children[0] = <div ref={dimensionsRef} key={'size-div'} style={{ width: 'max-content' }}>{firstChild}</div>;
   }

   const defaultMinWidth = '200px';
   const minColumnWidth = (width || defaultMinWidth) + 'px';

   const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : defined(props.fill) ? props.fill : true;
   const verticalFill = defined(props.verticalFill) ? props.verticalFill : defined(props.fill) ? props.fill : true;

   const style: CSSProperties & { '--uf-card-grid-panel--min-column-width': string } = {
      '--uf-card-grid-panel--min-column-width': minColumnWidth,
      ...props.style
   };

   let classNames = 'uf-card-grid-panel';

   const defaultGap = 'medium';
   const gap = props.gap === true ? defaultGap : props.gap;
   if (gap !== false) {
      classNames += ` uf-card-grid-panel-gap-${gap || defaultGap}`;
   }

   if (props.className) {
      classNames += ' ' + props.className;
   }

   return (
      <Panel
         className={classNames}
         border={props.border}
         padding={props.padding}
         paddingLeft={props.paddingLeft}
         paddingRight={props.paddingRight}
         paddingTop={props.paddingTop}
         paddingBottom={props.paddingBottom}
         width={props.width}
         height={props.height}
         minHeight={props.minHeight}
         minWidth={props.minWidth}
         maxHeight={props.maxHeight}
         maxWidth={props.maxWidth}
         verticalFill={verticalFill}
         horizontalFill={horizontalFill}
         style={style}
         ref={cardPanelRef}
         id={props.id}
      >
         {children}
      </Panel>
   );
};
